import { ConfigurationPageHeader } from '@modules/configuration/components/ConfigurationPageHeader';
import {
  configurationPageClass,
  configurationPageContentClass,
} from '@modules/configuration/components/configurationLayout';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Button } from '@shared/components/ui/button';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { HistoryAgGridTable, HistoryCardList } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { useHistoryAgGridColumns } from '../../hooks/useHistoryAgGridColumns';
import { useShippingRulePresetApplications } from '../../hooks/useShippingRulePresetApplications';

export const HistoryMain = () => {
  const { t } = useTranslation('shippingRulePresetsModule');
  const navigate = useNavigate();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [historySelectedColumns, setHistorySelectedColumns] = useState<string[]>([
    'preset',
    'targetTeam',
    'appliedBy',
    'appliedAt',
    'rulesInfo',
    'status',
  ]);

  const { applications, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useShippingRulePresetApplications();
  const { tanStackColumns: historyTanStackColumns } = useHistoryAgGridColumns(historySelectedColumns);

  useEffect(() => {
    if (!isLoading && isInitialLoad) setIsInitialLoad(false);
  }, [isLoading, isInitialLoad]);

  usePageNavigation({
    breadcrumbs: [{ label: t('title'), path: '/configuration/shipping-rule-presets' }, { label: t('history.title') }],
  });

  const goToPresets = useCallback(() => navigate('/configuration/shipping-rule-presets'), [navigate]);

  const headerAction = useMemo(
    () => (
      <Button variant="outline" onClick={goToPresets} data-testid="back-to-presets-button">
        {t('history.backToPresets')}
      </Button>
    ),
    [goToPresets, t],
  );

  if (isInitialLoad && isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <div className={`${configurationPageClass} pb-6`}>
        <ConfigurationPageHeader
          title={t('history.title')}
          description={t('history.description')}
          action={headerAction}
        />
        <div className={configurationPageContentClass}>
          <ErrorState error={error as Error} onRetry={refetch} />
        </div>
      </div>
    );
  }

  return (
    <div className={`${configurationPageClass} pb-6`}>
      <ConfigurationPageHeader
        title={t('history.title')}
        description={t('history.description')}
        action={headerAction}
      />

      <div className={configurationPageContentClass}>
        {!isMobile ? (
          <div className="mb-4">
            <ColumnSelector
              columns={historyTanStackColumns}
              selected={historySelectedColumns}
              onColumnsChange={setHistorySelectedColumns}
              storageKey="shipping-rule-presets-history-ag-grid-table"
            />
          </div>
        ) : null}

        {!isMobile ? (
          <div className={applications.length > 0 ? 'opacity-100 h-auto' : 'opacity-0 h-0 overflow-hidden'}>
            <HistoryAgGridTable
              applications={applications}
              selectedColumns={historySelectedColumns}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
            />
          </div>
        ) : (
          applications.length > 0 && (
            <div className={isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}>
              <HistoryCardList
                applications={applications}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />
            </div>
          )
        )}

        {applications.length === 0 && !isFetching && !isLoading && (
          <EmptyView type="emptyState" icon={EMPTY_VIEW_ICONS.DEFAULT} />
        )}
      </div>
    </div>
  );
};
